/**
 * 一過性ネットワークエラーの判定ユーティリティ。
 *
 * 背景 (#130 / #140 / #142 / #100 / #119):
 * 認証失効カスケード (refresh token 失効 → signOut) やページ遷移、Cloudflare
 * Workers / Supabase pooler の瞬断によって、進行中の fetch / RSC ストリームが
 * ネットワーク段階で中断されることがある。これらはユーザー操作で復帰可能な
 * 一過性事象であり、`console.error` で出すと PostHog が `$exception` として
 * 検知し、同一フィンガープリントの重複アラートを大量起票してしまう。
 *
 * 本物のバグ (描画クラッシュ等) は引き続き `console.error` で可視化したいので、
 * 「一過性ネットワークエラーか否か」を一元判定し、`warn` と `error` を出し分ける。
 */

// ブラウザ / ランタイムごとに文言が異なる fetch 系の一過性失敗。
const TRANSIENT_MESSAGE_PATTERNS: readonly RegExp[] = [
  /load failed/i, // Safari / WebKit の fetch 失敗
  /failed to fetch/i, // Chromium の fetch 失敗
  /networkerror/i, // Firefox
  /fetch is aborted/i, // AbortController.abort()
  /the (?:user )?operation was aborted/i, // AbortSignal.timeout 等
  /signal timed out/i, // AbortSignal.timeout() のタイムアウト (#224)
  /connection closed/i, // Cloudflare RSC ストリーム切断
  /the network connection was lost/i,
] as const;

/**
 * fetch / RSC のネットワーク段階で中断された一過性エラーかどうかを判定する。
 * AbortError / TimeoutError (DOMException) と上記文言パターンを一過性とみなす。
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof DOMException && isTransientErrorName(error.name)) {
    return true;
  }

  const name = getErrorName(error);
  if (name && isTransientErrorName(name)) return true;

  const message = getErrorMessage(error);
  if (!message) return false;
  return TRANSIENT_MESSAGE_PATTERNS.some((re) => re.test(message));
}

/**
 * クライアント側エラーをログ出力する。一過性ネットワークエラーは `console.warn`
 * (PostHog の $exception 対象外) に格下げし、それ以外は `console.error` で残す。
 */
export function logClientError(context: string, error: unknown): void {
  if (isTransientNetworkError(error)) {
    console.warn(`${context} (transient):`, getErrorMessage(error));
    return;
  }
  console.error(context, error);
}

// AbortController.abort() は AbortError、AbortSignal.timeout() は TimeoutError。
const TRANSIENT_ERROR_NAMES: readonly string[] = ["AbortError", "TimeoutError"];

function isTransientErrorName(name: string): boolean {
  return TRANSIENT_ERROR_NAMES.includes(name);
}

function getErrorName(error: unknown): string | undefined {
  if (error instanceof Error) return error.name;
  if (typeof error === "object" && error !== null && "name" in error) {
    const n = (error as { name?: unknown }).name;
    return typeof n === "string" ? n : undefined;
  }
  return undefined;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null && "message" in error) {
    const m = (error as { message?: unknown }).message;
    return typeof m === "string" ? m : "";
  }
  return "";
}
